/**
 * Listing Wizard Async Thunks
 * 
 * SOLID Principles Applied:
 * - Single Responsibility: Only handles async side effects (API calls)
 * - Separation of Concerns: Isolated from state management and data access
 * - Open/Closed: Easy to add new thunks without modifying existing ones
 * 
 * This file contains all async operations for the listing wizard feature:
 * - Fetch wizard snapshot (design + products)
 * - Save wizard section
 * - Publish listing
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { listingWizardService } from '../../../services/api/listingWizard.service';
import { formatErrorMessage } from '../../../utils/errorHandling';
import {
  IListingWizardSnapshot,
  IListingWizardSection,
  IListingWizardSectionData,
} from '../../../types/listing-wizard.types';

// ============================================
// ASYNC THUNKS (Side Effects)
// ============================================

/**
 * Fetch Listing Wizard Snapshot
 * 
 * Fetches all data needed to initialize the wizard:
 * - Design metadata
 * - Available products
 * - Current listing state (if exists)
 * 
 * @param designId - Design ID to create listing for
 * @returns Wizard snapshot data
 * 
 * Usage:
 *   dispatch(fetchListingWizardSnapshot(designId))
 */
export type FetchListingWizardSnapshotArg =
  | string
  | { designId: string; preserveStep?: boolean; silent?: boolean };

function resolveFetchSnapshotArg(arg: FetchListingWizardSnapshotArg): {
  designId: string;
  preserveStep: boolean;
  silent: boolean;
} {
  if (typeof arg === 'string') {
    return { designId: arg, preserveStep: false, silent: false };
  }
  return {
    designId: arg.designId,
    preserveStep: arg.preserveStep ?? false,
    silent: arg.silent ?? false,
  };
}

export function isSilentFetchSnapshotArg(arg: unknown): boolean {
  return (
    typeof arg === 'object' &&
    arg !== null &&
    'silent' in arg &&
    (arg as { silent?: boolean }).silent === true
  );
}

export const fetchListingWizardSnapshot = createAsyncThunk(
  'listingWizard/fetchSnapshot',
  async (arg: FetchListingWizardSnapshotArg, { rejectWithValue }) => {
    const { designId } = resolveFetchSnapshotArg(arg);
    try {
      console.log('📸 Fetching listing wizard snapshot for design:', designId);
      const snapshot = await listingWizardService.getSnapshot(designId);
      console.log('✅ Wizard snapshot fetched:', snapshot);
      return snapshot;
    } catch (error) {
      console.error('❌ Failed to fetch wizard snapshot:', error);
      const errorMessage = formatErrorMessage(error);
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Save Listing Wizard Section
 * 
 * Saves/updates a specific section of the wizard
 * Validates data and persists to database
 * 
 * @param params - Section ID and data to save
 * @returns Updated listing data
 * 
 * Usage:
 *   dispatch(saveListingSection({
 *     designId: 'uuid',
 *     section: 'identity',
 *     data: { title: '...', ... }
 *   }))
 */
export const saveListingSection = createAsyncThunk(
  'listingWizard/saveSection',
  async (
    params: {
      designId: string;
      section: IListingWizardSection;
      data: IListingWizardSectionData;
      silent?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      console.log('💾 Saving listing section:', {
        designId: params.designId,
        section: params.section,
      });
      
      const updated = await listingWizardService.saveSection(
        params.designId,
        params.section,
        params.data as any
      );
      
      console.log('✅ Section saved successfully');
      return { 
        section: params.section, 
        data: updated.sections?.[params.section] || params.data,
        listing: updated 
      };
    } catch (error) {
      console.error('❌ Failed to save section:', error);
      const errorMessage = formatErrorMessage(error);
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Save Listing Step
 * 
 * Saves the current step in the wizard to allow users to continue
 * from where they left off when they return.
 * 
 * @param designId - Design ID
 * @param step - Step number (0-8)
 * @returns Updated listing with current step
 * 
 * Usage:
 *   dispatch(saveListingStep({ designId: 'uuid', step: 3 }))
 */
export const saveListingStep = createAsyncThunk(
  'listingWizard/saveStep',
  async (
    params: { designId: string; step: number },
    { rejectWithValue }
  ) => {
    try {
      console.log('📍 Saving listing step:', {
        designId: params.designId,
        step: params.step,
      });
      
      const updated = await listingWizardService.saveStep(
        params.designId,
        params.step
      );
      
      console.log('✅ Step saved successfully');
      return { step: params.step, listing: updated };
    } catch (error) {
      console.error('❌ Failed to save step:', error);
      // Don't show error toast for step saving - it's a background operation
      return rejectWithValue(formatErrorMessage(error));
    }
  }
);

/**
 * Publish Listing
 * 
 * Publishes the listing to make it live on the store
 * Validates all required sections are complete
 * 
 * @param designId - Design ID to publish listing for
 * @param publishAt - Optional scheduled publish date
 * @returns Published listing data
 * 
 * Usage:
 *   dispatch(publishListing(designId))
 *   dispatch(publishListing(designId, new Date('2025-12-25')))
 */
export const publishListing = createAsyncThunk(
  'listingWizard/publish',
  async (
    params: { designId: string; publishAt?: Date },
    { rejectWithValue }
  ) => {
    try {
      console.log('🚀 Publishing listing:', {
        designId: params.designId,
        publishAt: params.publishAt,
      });
      
      const published = await listingWizardService.publish(
        params.designId,
        params.publishAt
      );
      
      console.log('✅ Listing published successfully');
      return published;
    } catch (error) {
      console.error('❌ Failed to publish listing:', error);
      const errorMessage = formatErrorMessage(error);
      return rejectWithValue(errorMessage);
    }
  }
);

